import { useSortedGoals } from 'features/goals';
import { useGoalsOverviewFilterDocument } from 'features/user';
import React, { useMemo, useRef } from 'react';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { PageEvents } from 'shared/services/tracking';
import {
  GoalGroupBy,
  GroupByMode,
  OrderDirection,
} from 'shared/types/sorting-mode';
import { GoalFilterList } from 'shared/views/goal-filter-list';

import { useGoalTreeReorder } from '../../hooks/use-goal-tree-reorder';
import { useGoalsOverviewOnSectionCollapse } from '../../hooks/use-goals-overview-on-section-collapse';
import { EmptyBanner } from '../empty-banner';
import { TabPanel } from '../tab-panel';
import * as Styled from './group-by-list.style';
import { SortBar } from './sort-bar';

export const GroupByList: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useTrackPageVisitEvent(PageEvents.GoalsList, undefined);
  const user = useUser();
  const sortedGoals = useSortedGoals();
  const showCompletedGoals =
    user?.settings?.goalsOverview?.roadmap?.showCompletedGoals ?? true;

  const filteredGoals = React.useMemo(() => {
    if (showCompletedGoals) {
      return sortedGoals;
    }
    return showCompletedGoals
      ? sortedGoals
      : sortedGoals.filter((g) => !g.completedAt);
  }, [sortedGoals, showCompletedGoals]);

  const filterDocument = useGoalsOverviewFilterDocument();
  const updateCollapsedSections = useGoalsOverviewOnSectionCollapse();

  const groupBy =
    (user?.settings?.goalGroupBy as GoalGroupBy) ?? GroupByMode.LifeArea;

  const onReorder = useGoalTreeReorder(groupBy);
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const customOrder = useMemo(
    () =>
      sortDirection === OrderDirection.Desc
        ? sortedGoals.map(({ id }) => id).toReversed()
        : sortedGoals.map(({ id }) => id),
    [sortedGoals, sortDirection],
  );

  if (!filteredGoals.length) {
    return <EmptyBanner />;
  }

  return (
    <TabPanel role="tab-panel" ref={scrollRef}>
      <Styled.GroupingContainer>
        <SortBar />
        <GoalFilterList
          filterDocument={filterDocument}
          onSectionCollapse={updateCollapsedSections}
          customOrder={customOrder}
          onReorder={onReorder}
          hideArchived={
            groupBy !== GroupByMode.None && groupBy !== GroupByMode.LifeArea
          }
          scrollElement={scrollRef}
          hideCompleted={
            !user?.settings?.goalsOverview?.roadmap?.showCompletedGoals
          }
        />
      </Styled.GroupingContainer>
    </TabPanel>
  );
};
